/**
 * Calendar-based "past event" (memory / archive) — independent of billing `retentionArchivedAt`.
 */

import { parseIsoDateOnly } from "@/lib/events/event-datetime"
import { parseWallTimeToHms } from "@/lib/events/wall-time"

export function isPastEventByStoredDate(dateStr: string): boolean {
  const raw = dateStr?.trim() ?? ""
  if (!raw) return false
  const ymd = parseIsoDateOnly(raw)
  if (!ymd) return false
  const eventDay = new Date(ymd.y, ymd.m - 1, ymd.d)
  if (Number.isNaN(eventDay.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  eventDay.setHours(0, 0, 0, 0)
  return eventDay < today
}

/** Local calendar date as `YYYY-MM-DD` (aligned with {@link isPastEventByStoredDate}). */
export function getLocalCalendarYmd(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/**
 * Coarse event phase for host UI. Uses wall-clock start (event date + optional time) through
 * end of that calendar day (local). “Live” is time-based only — not visibility.
 */
export type EventTimeStatusPhase = "upcoming" | "live" | "past"

function parseYmdLocal(dateStr: string): { y: number; mo: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim())
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2]) - 1
  const d = Number(m[3])
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null
  return { y, mo, d }
}

function eventLocalEndMs(ymd: { y: number; mo: number; d: number }): number {
  return new Date(ymd.y, ymd.mo, ymd.d, 23, 59, 59, 999).getTime()
}

export function getEventTimeStatusPhase(
  event: {
    date: string
    time?: string
  },
  now: Date = new Date(),
): EventTimeStatusPhase {
  const ymd = parseYmdLocal(event.date ?? "")
  if (!ymd) return "upcoming"

  const endMs = eventLocalEndMs(ymd)
  if (now.getTime() > endMs) return "past"

  const hms = parseWallTimeToHms(event.time)
  const startMs = hms
    ? new Date(ymd.y, ymd.mo, ymd.d, hms.h, hms.min, hms.s).getTime()
    : new Date(ymd.y, ymd.mo, ymd.d, 0, 0, 0, 0).getTime()

  if (now.getTime() < startMs) return "upcoming"
  return "live"
}
